// iter591 — изнесен от Dashboard.jsx (DIL бюджет + циркаден прозорец + info модал)
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Ruler } from "lucide-react";
import { useAuth } from "@/App";
import CircadianWindow from "@/components/dashboard/CircadianWindow";
import BodyMetricsEditModal from "@/components/dashboard/BodyMetricsEditModal";
import PerinatalDilBadge from "@/components/profile/PerinatalDilBadge";  // iter1108
import { budgetLine } from "@/utils/plainMeaning";  // iter1081

export default function InsulinBudgetMiniCard({ dailyData, mealDilData, dilBudget, isDark, bg, API, onDataChanged }) {
  const [showInfo, setShowInfo] = useState(false);
  const [showBodyEdit, setShowBodyEdit] = useState(false);
  const { user, refreshUser } = useAuth();
  const used = Math.round((mealDilData?.daily_effective_dil ?? mealDilData?.daily_raw_dil ?? 0) * 10) / 10;
  const total = Math.round((dilBudget || 97) * 10) / 10;
  const pct = Math.min(100, Math.round((used / Math.max(total, 1)) * 100));
  const remaining = Math.max(0, Math.round((total - used) * 10) / 10);
  const tone = pct < 70 ? "emerald" : pct < 100 ? "amber" : "rose";
  const toneClass = {
    emerald: { bar: "bg-emerald-500", text: isDark ? "text-emerald-300" : "text-emerald-700" },
    amber:   { bar: "bg-amber-500",   text: isDark ? "text-amber-300"   : "text-amber-700"   },
    rose:    { bar: "bg-rose-500",    text: isDark ? "text-rose-300"    : "text-rose-700"    },
  }[tone];
  return (
    <div
      className={`relative rounded-2xl border transition-all
        ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5 hover:border-emerald-500/30" : "bg-white border-gray-200 hover:border-emerald-200"}`}
      data-testid="cc-insulin-budget"
    >
      {/* Feb 2026 — Header row е сега flex layout, за да не се припокриват
          бутоните с дългото заглавие "ИНСУЛИНОГЕНЕН БЮДЖЕТ (DIL)".
          Заглавието се truncate-ва вместо да се overlay-ва. */}
      <div className="flex items-start gap-2 p-5 pb-3">
        <span className="text-xl leading-none mt-0.5 shrink-0" aria-hidden="true">🎯</span>
        <div className="min-w-0 flex-1">
          <h3 className={`text-sm font-bold uppercase tracking-wider leading-tight ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Инсулиногенен бюджет (DIL)" : "Insulinogenic budget (DIL)"}
          </h3>
          <div className="mt-1">
            <PerinatalDilBadge testid="cc-insulin-budget-perinatal-badge" />
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowBodyEdit(true); }}
          data-testid="cc-insulin-budget-edit-body"
          aria-label={bg ? "Промени телесни измервания и преизчисли" : "Edit body metrics & recalculate"}
          className={`h-6 px-2 rounded-full inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider transition-colors shrink-0 ${
            isDark ? "bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25" : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
          }`}
        >
          <Ruler className="w-3 h-3" />
          {bg ? "Данни" : "Metrics"}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowInfo(true); }}
          data-testid="cc-insulin-budget-info"
          aria-label={bg ? "Какво е инсулиногенен бюджет?" : "What is insulinogenic budget?"}
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors shrink-0 ${
            isDark ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          ?
        </button>
      </div>
      <Link
        to="/dashboard/nutrition-lab"
        className="block px-5 pb-5 group"
      >
        <div className="flex items-baseline gap-1.5 mb-2">
          <span className={`text-3xl font-extrabold tabular-nums ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{used}</span>
          <span className={`text-lg font-bold ${isDark ? "text-gray-500" : "text-gray-400"}`}>/ {total}</span>
          <span className={`text-xs font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}>g DIL</span>
        </div>
        <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
          <div className={`h-full transition-all duration-500 ${toneClass.bar}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className={toneClass.text}>{bg ? "Остават:" : "Remaining:"} <strong>{remaining} g</strong></span>
          <span className={`inline-flex items-center gap-1 font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"} group-hover:translate-x-0.5 transition-transform`}>
            ➜ Хранителна лаборатория
          </span>
        </div>
        {/* iter1081 — числото получава изречение на човешки език */}
        {bg && total > 0 && (
          <p className={`mt-1.5 text-[11px] leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}
             data-testid="cc-insulin-budget-plain">
            {budgetLine({ consumed: used, budget: total })}
          </p>
        )}
      </Link>

      {/* iter586 — Циркаден прозорец: inline expand, без модал */}
      {API && <CircadianWindow API={API} isDark={isDark} bg={bg} />}

      {/* Info модал — пациентски език */}
      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowInfo(false)} data-testid="cc-insulin-budget-modal">
          <div
            className={`w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${isDark ? "bg-[hsl(222,47%,12%)] text-white" : "bg-white text-[#1A1A2E]"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl" aria-hidden="true">🎯</span>
              <h3 className="text-lg font-bold">
                {bg ? "Какво е инсулиногенен бюджет (DIL)?" : "What is the insulinogenic budget (DIL)?"}
              </h3>
            </div>
            <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              {bg
                ? "Тялото ти отделя инсулин всеки път, когато ядеш. Колкото повече рафинирани въглехидрати и бързи протеини — толкова повече инсулин и толкова повече глад след това."
                : "Your body releases insulin every time you eat. The more refined carbs and fast-absorbing proteins — the more insulin, and the more hunger afterwards."}
            </p>

            {/* iter585 — DIL formula in plain language (per services/dil_formula.py) */}
            <div className={`mt-3 rounded-xl border p-3 text-xs leading-relaxed ${
              isDark ? "bg-violet-500/[0.06] border-violet-500/25 text-violet-100/90" : "bg-violet-50 border-violet-200 text-violet-950"
            }`}>
              {bg ? (
                <>
                  <strong>Базова формула:</strong> <span className="font-mono">DIL = (въглехидрати − фибри) + ~0.69 × протеин</span>
                  <br />
                  Това е <strong>базата</strong>. След това DIL се коригира от <strong>тип на протеина, тип на мазнините, време на хранене и хормонално състояние</strong> — затова една и съща храна може да даде различен DIL на различни хора и в различни часове.
                </>
              ) : (
                <>
                  <strong>Base formula:</strong> <span className="font-mono">DIL = (carbs − fiber) + ~0.69 × protein</span>
                  <br />
                  This is the <strong>baseline</strong>. DIL is then modulated by <strong>protein type, fat type, meal timing and hormonal status</strong> — same food, different DIL across people and hours.
                </>
              )}
            </div>

            {/* iter585 — RAISE vs LOWER, accurate to backend code */}
            <div className={`mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3`}>
              <div className={`rounded-xl border p-3 ${isDark ? "bg-rose-500/[0.07] border-rose-500/25" : "bg-rose-50 border-rose-200"}`}>
                <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-rose-200" : "text-rose-800"}`}>
                  ↑ {bg ? "Качват DIL" : "Raise DIL"}
                </div>
                <ul className={`text-xs leading-relaxed list-disc pl-4 space-y-0.5 ${isDark ? "text-rose-100/90" : "text-rose-900/90"}`}>
                  <li>{bg ? "рафинирани въглехидрати (бял хляб, тестени, ориз) и захари" : "refined carbs (white bread, pasta, rice) and sugars"}</li>
                  <li>{bg ? "бързи животински протеини: уей (×0.65), мляко/яйца (×0.60), червено месо/риба/птици (×0.55)" : "fast animal proteins: whey (×0.65), milk/eggs (×0.60), red meat/fish/poultry (×0.55)"}</li>
                  <li>{bg ? "мазнини + рафинирани въглехидрати (пица, поничка, кифла) — +15–25%, най-инсулиногенният реален сценарий" : "fat + refined carbs combo (pizza, donut, pastry) — +15–25%, the most insulinogenic real-world pattern"}</li>
                  <li>{bg ? "млечни мазнини (whey/левцинови пептиди) — +5–8%" : "dairy fats (whey/leucine peptides) — +5–8%"}</li>
                </ul>
              </div>
              <div className={`rounded-xl border p-3 ${isDark ? "bg-emerald-500/[0.07] border-emerald-500/25" : "bg-emerald-50 border-emerald-200"}`}>
                <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-emerald-200" : "text-emerald-800"}`}>
                  ↓ {bg ? "Свалят DIL" : "Lower DIL"}
                </div>
                <ul className={`text-xs leading-relaxed list-disc pl-4 space-y-0.5 ${isDark ? "text-emerald-100/90" : "text-emerald-900/90"}`}>
                  <li>{bg ? "фибри (зеленчуци, семена) — изваждат се директно от въглехидратите" : "fiber (vegetables, seeds) — directly subtracted from carbs"}</li>
                  <li>{bg ? "растителни протеини: ядки/семена/зеленчуци (×0.40), бобови/леща (×0.45), соя/тофу (×0.50)" : "plant proteins: nuts/seeds/vegetables (×0.40), legumes/lentils (×0.45), soy/tofu (×0.50)"}</li>
                  <li>{bg ? "колаген и желатин (×0.30) — най-нисък инсулинов отговор" : "collagen & gelatin (×0.30) — lowest insulin response"}</li>
                  <li>{bg ? "MCT и кокосово масло — неутрални за инсулина (хепатична оксидация)" : "MCT & coconut oil — insulin-neutral (hepatic oxidation)"}</li>
                </ul>
              </div>
            </div>

            {/* iter585 — Circadian + menstrual cycle (per services/circadian_metabolism + dil_audit) */}
            <div className={`mt-4 rounded-xl border p-3 text-xs leading-relaxed ${
              isDark ? "bg-amber-500/[0.06] border-amber-500/25 text-amber-100/95" : "bg-amber-50 border-amber-200 text-amber-950"
            }`}>
              <div className="font-bold mb-1.5">
                ⏱ {bg ? "Време и цикъл — същата храна, различен DIL" : "Timing & cycle — same food, different DIL"}
              </div>
              <div className="mb-1.5">
                <strong>{bg ? "Циркаден ефект:" : "Circadian effect:"}</strong>
                <ul className="list-disc pl-4 space-y-0.5 mt-0.5">
                  <li>{bg ? "Сутрин (06–10 ч.) ×1.00 — пик на инсулинова чувствителност" : "Morning (06–10) ×1.00 — peak insulin sensitivity"}</li>
                  <li>{bg ? "Обед (10–14 ч.) ×1.05 · Следобед (14–18 ч.) ×1.10" : "Midday (10–14) ×1.05 · Afternoon (14–18) ×1.10"}</li>
                  <li>{bg ? "Вечер (18–22 ч.) ×1.20 — мелатонинът се покачва" : "Evening (18–22) ×1.20 — melatonin rising"}</li>
                  <li>{bg ? "Биологична нощ (22–06 ч.) ×1.35 — максимален товар" : "Biological night (22–06) ×1.35 — peak load"}</li>
                </ul>
              </div>
              <div>
                <strong>{bg ? "Менструален цикъл (за жени):" : "Menstrual cycle (women):"}</strong>
                <ul className="list-disc pl-4 space-y-0.5 mt-0.5">
                  <li>{bg ? "Фоликуларна (1–13 ден) ×0.95 — по-висока инсулинова чувствителност" : "Follicular (1–13) ×0.95 — higher insulin sensitivity"}</li>
                  <li>{bg ? "Овулаторна (14–16 ден) ×1.00" : "Ovulatory (14–16) ×1.00"}</li>
                  <li>{bg ? "Лутеална (17–28 ден) ×1.10 — естествено повишена резистентност" : "Luteal (17–28) ×1.10 — naturally higher resistance"}</li>
                </ul>
                <p className="mt-1 opacity-90">
                  {bg
                    ? "Висок естроген ×0.92 (защитен ефект) · умерен ×0.96 · нисък/мъже ×1.00."
                    : "High estrogen ×0.92 (protective) · moderate ×0.96 · low/male ×1.00."}
                </p>
              </div>
            </div>

            {/* iter585 — Perinatal stages (bedside-safe reminder) */}
            <div className={`mt-3 rounded-xl border p-3 text-xs leading-relaxed ${
              isDark ? "bg-pink-500/[0.06] border-pink-500/25 text-pink-100/95" : "bg-pink-50 border-pink-200 text-pink-950"
            }`}>
              <strong>🤰 {bg ? "Специални режими — бременност и кърмене:" : "Special modes — pregnancy & lactation:"}</strong>
              <ul className="list-disc pl-4 space-y-0.5 mt-0.5">
                <li>{bg ? "Бременност T1 (1–13 седм.): +70 kcal/ден, ×1.02 на DIL" : "Pregnancy T1 (wk 1–13): +70 kcal/d, DIL ×1.02"}</li>
                <li>{bg ? "Бременност T2 (14–27 седм.): +340 kcal/ден, ×1.10" : "Pregnancy T2 (wk 14–27): +340 kcal/d, ×1.10"}</li>
                <li>{bg ? "Бременност T3 (28+ седм.): +452 kcal/ден, ×1.15; протеин до 1.3 g/kg" : "Pregnancy T3 (wk 28+): +452 kcal/d, ×1.15; protein up to 1.3 g/kg"}</li>
                <li>{bg ? "Изключително кърмене: по-висок таван на въглехидратите за млечна продукция" : "Exclusive lactation: higher carb ceiling for milk supply"}</li>
              </ul>
              <p className="mt-1 opacity-90">
                {bg ? "Тези режими отварят отделен бюджет — личният ти DIL се преизчислява автоматично." : "These modes open a dedicated budget — your personal DIL is recomputed automatically."}
              </p>
            </div>

            {/* iter585 — DIL vs NIS quick distinction (per PyPI doc) */}
            <div className={`mt-4 rounded-xl border p-3 text-xs leading-relaxed ${
              isDark ? "bg-white/[0.03] border-white/10 text-gray-200" : "bg-gray-50 border-gray-200 text-gray-700"
            }`}>
              <strong>{bg ? "DIL vs NIS — два допълващи се индикатора:" : "DIL vs NIS — two complementary metrics:"}</strong>
              <ul className="mt-1 space-y-0.5">
                <li>• <strong>DIL</strong> ({bg ? "грамове, дневен" : "grams, daily"}) — {bg ? "общият товар за деня (числото горе)" : "total daily burden (the number above)"}</li>
                <li>• <strong>NIS</strong> ({bg ? "0–100%, на хранене" : "0–100%, per meal"}) — {bg ? "колко силно конкретно хранене стимулира инсулина" : "how intensely a single meal stimulates insulin"}</li>
              </ul>
            </div>

            <p className={`mt-3 text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              <strong>{bg ? "Как се изчислява личният ти бюджет:" : "How your personal budget is set:"}</strong> {bg
                ? "Според Вашето тегло, цел (отслабване, поддръжка, изграждане), хормонално състояние, метаболитен профил и текуща фаза на цикъла или бременността/кърменето."
                : "Based on your weight, goal, hormonal state, metabolic profile and current cycle phase or pregnancy/lactation stage."}
            </p>
            {/* iter583 — Methodology link */}
            <a
              href="https://pypi.org/project/nutramilo-nis/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cc-insulin-budget-methodology-link"
              className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${
                isDark ? "text-violet-300 hover:text-violet-100" : "text-violet-700 hover:text-violet-900"
              }`}
            >
              {bg ? "Базирано на Nutramilo-NIS методологията →" : "Built on the Nutramilo-NIS methodology →"}
            </a>
            <div className="mt-5 flex gap-2">
              <Link
                to="/dashboard/plan"
                onClick={() => setShowInfo(false)}
                className={`flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-full text-sm font-bold ${
                  isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-100 text-emerald-900 hover:bg-emerald-200"
                }`}
              >
                {bg ? "Виж моя план →" : "View my plan →"}
              </Link>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className={`px-4 py-2.5 rounded-full text-sm font-semibold ${
                  isDark ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-[#1A1A2E]"
                }`}
              >
                {bg ? "Затвори" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Body Metrics Edit Modal — Bulgarian UX request Feb 2026 */}
      <BodyMetricsEditModal
        open={showBodyEdit}
        onClose={() => setShowBodyEdit(false)}
        API={API}
        user={user}
        isDark={isDark}
        bg={bg}
        onSaved={async () => {
          if (refreshUser) await refreshUser();
          if (onDataChanged) onDataChanged();
        }}
      />
    </div>
  );
}
